/**
 * Contrato de datos del lobby (lista de salas + matchmaking).
 *
 * Esta es la ÚNICA fuente de verdad del formato que comparten:
 *   - la UI mock (Fase M0, contra datos falsos)
 *   - el servidor real (Colyseus + Redis + CDN, Fases M1–M3)
 *
 * Diseñarlo ANTES de la UI es diseñar la API del servidor: cuando el shell
 * sabe renderizar estas estructuras, el backend solo tiene que emitirlas.
 *
 * Reglas de seguridad embebidas en el tipo (ver docs/ROADMAP-MANUFACTURA.md §1.3):
 *   - NUNCA viaja una IP al cliente. `hostCountry` es un código de país
 *     derivado en el servidor vía GeoIP.
 *   - El ping NO va en el payload de la sala: se mide en el cliente por región
 *     (probe a los edges regionales) y se anota sobre la sala en render.
 */

/** Regiones de despliegue (edges de Hathora/Agones). Ampliable sin romper la UI. */
export type RegionId =
  | "us-east"
  | "us-west"
  | "eu-west"
  | "eu-central"
  | "sa-east"
  | "ap-south"
  | "ap-southeast";

/** ISO 3166-1 alpha-2 en minúsculas ("ar", "es", "us"). Derivado server-side. */
export type CountryCode = string;

/** Estado de una sala tal como lo publica el game server a Redis/CDN. */
export type RoomStatus = "waiting" | "starting" | "in-progress" | "full";

/**
 * Resumen de una sala para la lista pública.
 *
 * IMPORTANTE: este objeto se sirve desde CDN cacheado (agregado de los
 * heartbeats de los game servers), NO consultando cada game server en vivo.
 * Por eso no lleva datos volátiles por-cliente (como el ping).
 */
export interface RoomSummary {
  /** Id opaco de sala. El cliente lo usa para unirse; no revela host ni IP. */
  id: string;
  name: string;
  /** País del host, derivado por GeoIP en el servidor. Nunca la IP. */
  hostCountry: CountryCode;
  /** Región del edge que hospeda la sala (para calcular el ping client-side). */
  region: RegionId;
  players: number;
  capacity: number;
  status: RoomStatus;
  /** Debe coincidir con un id de MAPS en game.ts ("classic", "big", ...). */
  mapId: string;
  hasPassword: boolean;
  /**
   * Qué clase de partido es (ver `RoomKind` en @tak/sim):
   *  - "official": la montó el emparejamiento rápido.
   *  - "friendly": la montó una persona en la región que eligió — un PARTIDO NO
   *    OFICIAL, para jugar con conocidos y con ping de casa.
   * Opcional: un edge antiguo puede no mandarlo, y entonces no se promete nada.
   */
  kind?: "official" | "friendly";
}

/**
 * Snapshot de la lista de salas. Incluye metadata de caché porque se sirve
 * desde el edge: el cliente respeta `ttlMs` antes de re-pedir.
 */
export interface RoomListSnapshot {
  rooms: RoomSummary[];
  /** Epoch ms en que el edge generó este agregado. */
  fetchedAt: number;
  /** Cuánto puede cachear el cliente antes de refrescar. */
  ttlMs: number;
}

/** Cómo ordenar la lista de salas (MM-1). */
export type RoomSort = "ping" | "players" | "name";

/** Filtro opcional aplicado del lado cliente (o como query al edge). */
export interface RoomFilter {
  region?: RegionId;
  /** Solo partidos amistosos (los que hostea la gente) o solo oficiales. */
  kind?: "official" | "friendly";
  /** Oculta las salas donde ya no cabe nadie. Ojo: "en curso" NO es "llena" —
   *  a una partida empezada con hueco sí se puede entrar. */
  hideFull?: boolean;
  mapId?: string;
  /** Búsqueda por nombre de sala (sin distinguir mayúsculas ni acentos). */
  query?: string;
  /** Orden de la lista. Por defecto, por ping. */
  sort?: RoomSort;
}

/** RTT medido en el cliente contra el edge de una región (no entre peers). */
export interface RegionPing {
  region: RegionId;
  rttMs: number;
}

/**
 * Identidad del jugador (anónima primero, ver §5.2).
 * `accountId` es la fuente de verdad server-side de la que cuelgan skins,
 * mapas y progreso; sobrevive al link con Google/Apple/Steam (cross-save).
 */
export interface PlayerIdentity {
  accountId: string;
  /** El nombre que eligió la persona en la pantalla de entrada. */
  displayName: string;
  /** true hasta que se linkea una cuenta de tienda. */
  isGuest: boolean;
  /** País, si el servidor lo ha derivado. El cliente NO se lo inventa. */
  country?: CountryCode;
}

/**
 * Estados de conexión/matchmaking que la UI DEBE saber renderizar.
 * Hoy no existen (no hay red); definirlos ahora evita rehacer la UI en M6.
 */
export type ConnectionState =
  | "idle"
  | "authenticating"
  | "matchmaking"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "region-unavailable"
  | "server-error"
  | "disconnected";

/**
 * Fuente de datos del lobby. La UI depende de ESTA interfaz, no de una
 * implementación concreta. En M0 la cumple un mock; en M1–M3 la cumple el
 * cliente real (Colyseus/CDN) sin tocar la capa de vista.
 */
export interface RoomListProvider {
  /** Lista de salas (mock → CDN real). */
  listRooms(filter?: RoomFilter): Promise<RoomListSnapshot>;
  /** RTT por región para anotar el ping sobre cada sala. */
  pingRegions(): Promise<RegionPing[]>;
}

/** Une una sala con el ping medido para su región (lo que ve el jugador). */
export interface RoomWithPing extends RoomSummary {
  pingMs: number | null;
}

/**
 * Anota cada sala con el ping medido de su región. Función pura y compartible:
 * la usa tanto la UI mock como la real, así el "cómo se calcula el ping
 * mostrado" vive en un solo sitio.
 */
export function annotatePing(
  rooms: readonly RoomSummary[],
  pings: readonly RegionPing[],
): RoomWithPing[] {
  const byRegion = new Map<RegionId, number>();
  for (const p of pings) byRegion.set(p.region, p.rttMs);
  return rooms.map((r) => ({
    ...r,
    pingMs: byRegion.has(r.region) ? byRegion.get(r.region)! : null,
  }));
}

/**
 * MM-1 · Normaliza texto para buscar: sin mayúsculas y sin acentos.
 *
 * Sin quitar los acentos, buscar "campeon" no encuentra "Campeón", que es
 * exactamente lo que la gente escribe cuando teclea deprisa en el móvil.
 */
export function normalizeSearch(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/**
 * MM-1 · ¿Pasa esta sala el filtro? Pura y compartida por TODOS los providers
 * (mock, un edge, multi-región), para que el lobby se comporte igual sea cual
 * sea la fuente de datos.
 */
export function passesFilter(room: RoomSummary, f: RoomFilter): boolean {
  if (f.region && room.region !== f.region) return false;
  // "Ocultar llenas" oculta solo las LLENAS. Una partida en curso con hueco
  // sigue siendo unible, y esconderla es esconder salas a las que se puede entrar.
  if (f.hideFull && room.status === "full") return false;
  if (f.mapId && room.mapId !== f.mapId) return false;
  // Tipo de partido. Una sala sin `kind` (edge antiguo) se cuenta como oficial:
  // es lo que era todo antes de que existieran los amistosos.
  if (f.kind && (room.kind ?? "official") !== f.kind) return false;
  if (f.query) {
    const q = normalizeSearch(f.query);
    if (q && !normalizeSearch(room.name).includes(q)) return false;
  }
  return true;
}

/**
 * MM-1 · Ordena la lista ya anotada con el ping.
 *
 * Por defecto manda el ping, que es lo que más determina si una partida se va a
 * poder jugar. Las salas SIN ping medido van al final en vez de tratarse como
 * ping 0: no sabemos si están cerca, y colarlas arriba sería recomendar a ciegas.
 */
export function sortRooms(rooms: readonly RoomWithPing[], sort: RoomSort = "ping"): RoomWithPing[] {
  const out = [...rooms];
  if (sort === "name") {
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }
  if (sort === "players") {
    // Más gente primero: una sala con alguien esperando es mejor que una vacía.
    out.sort((a, b) => b.players - a.players || a.name.localeCompare(b.name));
    return out;
  }
  out.sort((a, b) => {
    const ap = a.pingMs ?? Number.POSITIVE_INFINITY;
    const bp = b.pingMs ?? Number.POSITIVE_INFINITY;
    return ap - bp || a.name.localeCompare(b.name);
  });
  return out;
}
